import type { ContextBlock, PipelineStage } from '../types';
import { draftText } from '../draft-text';
import { findSymbol } from '../../parsing/symbol-index';

const IDENTIFIER_RE = /\b[A-Za-z_$][A-Za-z0-9_$]*\b/g;
const MIN_NAME_LENGTH = 3; // skip short/common tokens (i, id, x, ...) to cut false positives

export const a2DependencyStubs: PipelineStage = {
  id: 'A2',
  group: 'A',
  label: 'Dependency signature stubs',
  description:
    'For symbols referenced by extracted code but defined elsewhere, include only their signature and docstring, not the implementation.',
  impact: 'High',
  defaultEnabled: true,
  implemented: true,

  async run(draft, _params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));

    const blockContentByPath = new Map(
      draft.openFiles.map((f) => [f.path, draft.contextBlocks.find((b) => b.id === `file:${f.path}`)?.content ?? '']),
    );

    const combinedContext = draft.contextBlocks.map((b) => b.content).join('\n');
    const identifiers = new Set((combinedContext.match(IDENTIFIER_RE) ?? []).filter((n) => n.length >= MIN_NAME_LENGTH));

    const stubs: string[] = [];
    const included = new Set<string>();

    for (const name of identifiers) {
      for (const sym of findSymbol(name)) {
        if (included.has(`${sym.path}:${sym.name}`)) continue;
        // If the symbol's own file is still present in full (or its signature
        // literally appears in that file's current context block), it's
        // already available — don't duplicate it as a stub.
        const currentBlock = blockContentByPath.get(sym.path);
        const headerOnly = sym.signature.split('{')[0].trim();
        if (currentBlock && currentBlock.includes(headerOnly)) continue;

        included.add(`${sym.path}:${sym.name}`);
        stubs.push(`${sym.docstring ? sym.docstring + '\n' : ''}${sym.signature}  // from ${sym.path}`);
      }
    }

    if (stubs.length === 0) {
      return {
        draft,
        report: {
          stageId: 'A2',
          tokensBefore,
          tokensAfter: tokensBefore,
          tokensSaved: 0,
          notes: ['No referenced symbols found defined elsewhere in the indexed project.'],
        },
      };
    }

    const stubBlock: ContextBlock = {
      id: 'a2:dependency-stubs',
      label: 'Referenced symbols (signatures only)',
      content: stubs.join('\n\n'),
      cacheable: true,
    };
    const newDraft = { ...draft, contextBlocks: [...draft.contextBlocks, stubBlock] };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'A2',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes: [
          `Added ${stubs.length} referenced symbol signature(s) instead of pulling in their full defining file(s).`,
        ],
      },
    };
  },
};
